/**
 * PATH: apps/qa/src/pages/new-inspection/inspection-types.ts
 * PURPOSE: Types for new inspection wizard — jobs, floors, areas, AI preselection
 */

import { Building2, ClipboardCheck, FileSpreadsheet, MapPin, Check } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// ─────────────────────────────────────────────────────────────────────────────
// Toast
// ─────────────────────────────────────────────────────────────────────────────

export interface ToastNotification {
  id: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  action?: { label: string; onClick: () => void }
}

// ─────────────────────────────────────────────────────────────────────────────
// Job
// ─────────────────────────────────────────────────────────────────────────────

export interface Site { id?: number; name?: string; address?: string }
export interface Client { id?: number; name?: string }

export interface Job {
  job_id: number
  job_number?: string
  quotech_job_id?: number | null
  status?: string | null
  combined_status?: string | null
  type?: string | null
  site?: Site | string | null
  client?: Client | string | null
  project_manager?: string | null
  tags?: string[]
  display_name?: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// AI Pre-selection
// ─────────────────────────────────────────────────────────────────────────────

export interface AIPreselection {
  category: 'residential' | 'commercial' | 'infrastructure'
  projectType: 'new_construction' | 'renovation' | 'remediation'
  buildingClass: string | null
  tradeTags: string[]
  confidence: number
  reason: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Floor / Area Drafts
// ─────────────────────────────────────────────────────────────────────────────

export interface LocalAttachment {
  id: string
  file: File
  type: 'photo' | 'document'
  preview: string | null
  name: string
}

export interface FloorDraft {
  id: string
  floor_name: string
  areas: AreaDraft[]
  notes: string
}

export interface AreaDraft {
  id: string
  area_name: string
  area_code: string
  notes: string
  trade: string
  attachments: LocalAttachment[]
  isExpanded: boolean
}

// ─────────────────────────────────────────────────────────────────────────────
// Steps
// ─────────────────────────────────────────────────────────────────────────────

export type InspectionStepId = 'job' | 'source' | 'details' | 'structure' | 'review'

export interface InspectionStep {
  id: InspectionStepId
  label: string
  icon: LucideIcon
}

export const STEPS: InspectionStep[] = [
  { id: 'job', label: 'Select Job', icon: Building2 },
  { id: 'source', label: 'Source', icon: FileSpreadsheet },
  { id: 'details', label: 'Details', icon: ClipboardCheck },
  { id: 'structure', label: 'Floors & Areas', icon: MapPin },
  { id: 'review', label: 'Review', icon: Check },
]

/** Position of a step. Callers address steps by id so inserting one here
 *  cannot silently renumber navigation, guards or the Review edit links. */
export function stepIndex(id: InspectionStepId): number {
  const index = STEPS.findIndex((step) => step.id === id)
  if (index < 0) throw new Error(`unknown inspection step: ${id}`)
  return index
}

/** Step id at a position, falling back to the first step for out-of-range
 *  values so a stale index can never render a blank wizard body. */
export function stepIdAt(index: number): InspectionStepId {
  return STEPS[index]?.id ?? STEPS[0].id
}
